import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { track } from "../analytics";
import { safeCopy } from "../lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "fgm_popup_seen";
const DELAY_MS = 30000;  // 30s — less aggressive than the original 8s

export default function EmailPopup() {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [code, setCode] = useState(null);

    useEffect(() => {
        if (typeof window === "undefined") return;
        let seen;
        try {
            seen = localStorage.getItem(STORAGE_KEY);
        } catch {
            seen = null;
        }
        if (seen) return;

        let triggered = false;
        const trigger = () => {
            if (triggered) return;
            triggered = true;
            setOpen(true);
        };

        // (1) Delayed trigger — 30s on the page
        const timeout = setTimeout(trigger, DELAY_MS);

        // (2) Exit-intent trigger — desktop only (mouse leaves top of viewport)
        const onMouseOut = (e) => {
            if (!e.relatedTarget && e.clientY < 10) trigger();
        };
        document.addEventListener("mouseout", onMouseOut);

        return () => {
            clearTimeout(timeout);
            document.removeEventListener("mouseout", onMouseOut);
        };
    }, []);

    const dismiss = (markSeen = true) => {
        setOpen(false);
        if (markSeen) {
            try {
                localStorage.setItem(STORAGE_KEY, "1");
            } catch {
                /* ignore */
            }
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) {
            toast.error("Drop a valid email");
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/popup/signup`, { email: email.trim().toLowerCase() });
            track("sign_up", { source: "welcome_popup" });
            setCode(res.data?.code || "WELCOME10");
            toast.success("You're in. Check your inbox for your code 🔥");
            try {
                localStorage.setItem(STORAGE_KEY, "1");
            } catch {
                /* ignore */
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Signup failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="email-popup"
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-[fadeIn_0.3s_ease-out]"
            onClick={(e) => {
                if (e.target === e.currentTarget) dismiss(true);
            }}
        >
            <div
                className="relative bg-fgm-surface border border-[#E63946]/40 max-w-md w-full p-6 sm:p-8 shadow-[0_0_40px_rgba(230,57,70,0.2)]"
                style={{ background: "linear-gradient(180deg, #1a0a0d 0%, #0a0a0a 100%)" }}
            >
                <button
                    data-testid="email-popup-close"
                    onClick={() => dismiss(true)}
                    className="absolute top-2 right-3 text-zinc-500 hover:text-white text-2xl leading-none"
                    aria-label="Close"
                >
                    ×
                </button>

                {!code ? (
                    <>
                        <div className="text-[10px] font-display tracking-[0.4em] uppercase text-[#E63946] mb-3">
                            ⚡ Summer Heat Drop Loading
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-white uppercase leading-tight">
                            Get 10% Off
                            <br />
                            Your First Order
                        </h2>
                        <p className="text-zinc-400 text-sm mt-3 leading-relaxed">
                            Drop your email. We&apos;ll send you a one-time code + first dibs on every drop. No spam — only fire.
                        </p>

                        <form onSubmit={submit} className="mt-5 space-y-3">
                            <input
                                data-testid="email-popup-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@gmail.com"
                                autoFocus
                                required
                                className="w-full bg-black border border-white/20 px-4 py-3 text-white text-sm focus:border-[#E63946] focus:outline-none"
                            />
                            <button
                                data-testid="email-popup-submit"
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-[#E63946] text-white font-display tracking-[0.3em] uppercase text-sm py-3 hover:bg-white hover:text-[#E63946] transition-colors disabled:opacity-50"
                            >
                                {submitting ? "Claiming…" : "Claim My Code"}
                            </button>
                            <button
                                type="button"
                                data-testid="email-popup-skip"
                                onClick={() => dismiss(true)}
                                className="w-full text-zinc-500 hover:text-white text-[10px] font-display tracking-[0.3em] uppercase py-2"
                            >
                                Skip — Maybe Later
                            </button>
                        </form>
                        <p className="text-[10px] text-zinc-600 mt-3 text-center">
                            First-time customers · 7-day expiry · Stripe-validated
                        </p>
                    </>
                ) : (
                    <>
                        <div className="text-[10px] font-display tracking-[0.4em] uppercase text-[#E63946] mb-3">
                            🔥 You&apos;re In
                        </div>
                        <h2 className="font-display text-3xl tracking-tight text-white uppercase">
                            Welcome to the Movement
                        </h2>
                        <p className="text-zinc-400 text-sm mt-3">
                            Your personal 10% off code is ready. We just sent it to <span className="text-white">{email}</span> — but here it is right now too:
                        </p>
                        <div className="my-5 p-4 border-2 border-dashed border-[#E63946] text-center">
                            <div className="text-[10px] font-display tracking-[0.3em] uppercase text-zinc-500 mb-1">
                                Your Code
                            </div>
                            <div
                                data-testid="email-popup-code"
                                className="text-2xl font-mono tracking-widest text-white font-bold cursor-pointer"
                                onClick={async () => {
                                    const ok = await safeCopy(code);
                                    if (ok) toast.success("Copied to clipboard");
                                    else toast.error("Copy blocked — please copy manually");
                                }}
                                title="Click to copy"
                            >
                                {code}
                            </div>
                        </div>
                        <button
                            data-testid="email-popup-shop"
                            onClick={() => {
                                dismiss(true);
                                window.location.hash = "#shop";
                            }}
                            className="w-full bg-[#E63946] text-white font-display tracking-[0.3em] uppercase text-sm py-3 hover:bg-white hover:text-[#E63946] transition-colors"
                        >
                            Shop Summer Heat Drop
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
